import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Dropdown, Input } from 'antd';

import TagBadge, { TagRow } from '../tags/TagBadge';
import TagMoveModal from '../tags/TagMoveModal';
import RecordBadge from '../common/RecordBadge';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import { Skeleton } from '../loading';
import { buildActionMenu } from '../common/actionMenu';
import useTagSearch from '../../hooks/useTagSearch';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { turnOnItemTag } from '../../services/itemTags';
import { DIAGRAM_2, KEBAB, MAGNIFYING_GLASS } from '../icons';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { ITEM_TAG_MODE_LABELS, itemTagMoveRefusal, itemTagPlaceTargets, itemTagPlacement } from '../../utils/itemTagSettings';
import { tagIdOf, tagParentIdOf, tagPathLabel, treeOrder } from '../../utils/tags';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * ITEM TAGS in Settings › Tags (docs/plans/tags.md §5.2: "Item tags appear as
 * one row per opted card (a count and a search), never as a full list";
 * build step T4d). A card of a thousand customers used as tags would be a
 * list of a thousand, so the vocabulary never lists them (keepr-api §
 * Tags: `itemTagCards`, one row per card that uses its items as tags in the
 * collection or an ancestor the reader reads): each row is the card, its
 * setting, how many of its item tags the reader can see, and a search that
 * finds one by its item's title (`GET /api/tags/search?collection_id&card_id`:
 * the API answers that card's item tags alone, and those of the cards that
 * extend it, with its own cap and paging — nothing is filtered here).
 *
 * A found item tag is its item's badge, linking to the item — where Stop
 * using as a tag lives. The setting itself is changed on the card's row in
 * Cards (the link on each row).
 *
 * MOVE TO… (ruled b58, d28): for a manager (`canManage` — manage, and the
 * collection not archived), a found item tag whose ITEM lives in this
 * collection is a row with where it sits ("Under Digestive") and a ⋯ with
 * **Move to…** (Record): under one of this collection's own tags at level 1
 * or 2 (an item tag is always the last of three levels), never a restricted
 * one, or back to the top level — `PUT /api/items/{itemId}/own-tag
 * { parentId, position }` (services/itemTags.js turnOnItemTag, the same
 * idempotent call as Use as a tag). A parent holds at most 200 item tags; the
 * API's refusals are said in plain words (itemTagMoveRefusal). Anyone else,
 * and an item tag of another collection (a parent's card row), keeps the
 * plain chips.
 */
function CardRow({ row, collectionId, ownerName, cardsById, tags = [], canManage = false }) {
	const { message } = App.useApp();
	const [query, setQuery] = useState('');
	// Where a moved item tag sits now (tag id → parent id), over the search's
	// kept answer, which still holds the old place.
	const [placed, setPlaced] = useState({});
	const [moving, setMoving] = useState(null); // the item tag
	const cardId = normalizeId(row?.card_id);
	const rowCollectionId = normalizeId(row?.collection_id);
	const card = cardsById ? cardsById[cardId] || null : null;
	const cardName = card?.name || 'A card';
	// The API keeps to this card and the cards that extend it (`card_id`).
	const { itemTags: found, loading, more } = useTagSearch(query, { collectionId, cardId });

	const resolveItemTag = tag => ({
		record: cardsById ? cardsById[normalizeId(tag.card_id)] || card : card,
		title: tag.title || 'Untitled item',
		collection: tag.collection_id || null,
	});

	const count = Number(row?.count) || 0;
	const typed = query.replace(/\s+/g, ' ').trim();
	const ordered = useMemo(() => treeOrder(tags), [tags]);
	const results = found.map(result => {
		const tag = { ...result.tag, collection_id: normalizeId(result.collection?._id) || rowCollectionId };
		const id = tagIdOf(tag);
		return Object.prototype.hasOwnProperty.call(placed, id) ? { ...tag, parentId: placed[id] } : tag;
	});
	// Move to… is offered on an item tag whose item lives in this collection:
	// its places are this collection's own tags.
	const movable = tag => canManage && Boolean(normalizeId(tag?.item_id)) && normalizeId(tag?.collection_id) === collectionId;
	const anyMovable = results.some(movable);
	const itemLink = tag => (tag.item_id ? `/collections/${tag.collection_id}/items/${normalizeId(tag.item_id)}` : undefined);
	const titleOf = tag => tag?.title || 'Untitled item';
	const placeOf = tag => {
		const parentId = tagParentIdOf(tag);
		if (!parentId) return null;
		const parent = ordered.find(entry => tagIdOf(entry) === parentId);
		return parent ? `Under ${tagPathLabel(parent)}` : null;
	};

	const moveTo = async parentId => {
		const tag = moving;
		const body = itemTagPlacement(tags, parentId);
		const saved = await turnOnItemTag(normalizeId(tag.item_id), body);
		setPlaced(prev => ({ ...prev, [tagIdOf(tag)]: saved ? tagParentIdOf(saved) : body.parentId }));
		const parent = body.parentId ? ordered.find(entry => tagIdOf(entry) === body.parentId) : null;
		message.success(parent ? `Moved “${titleOf(tag)}” under “${parent.name}”.` : `Moved “${titleOf(tag)}” to the top level.`);
	};
	return (
		<li className="item-tag-cards__row" data-testid="item-tag-card-row" data-card={cardId}>
			<div className="item-tag-cards__head">
				<RecordBadge kind="card" record={card || { name: cardName }} title={cardName} size="sm" />
				<span className="meta-line item-tag-cards__meta">
					{[
						ITEM_TAG_MODE_LABELS[row.mode] || null,
						count ? `${formatCount(count, 'item tag')}` : 'No item tags yet',
						ownerName ? `From ${ownerName}` : null,
					].filter(Boolean).join(' · ')}
				</span>
				{!ownerName ? (
					<Link className="item-tag-cards__change" to={`/collections/${collectionId}/cards`}>Change on the Cards tab</Link>
				) : null}
			</div>
			<Input
				{...NO_TEXT_ASSIST}
				allowClear
				className="item-tag-cards__search"
				prefix={<MAGNIFYING_GLASS aria-hidden="true" />}
				placeholder={`Find a ${cardName} item tag by its title`}
				aria-label={`Find an item tag of ${cardName}`}
				value={query}
				maxLength={60}
				onChange={event => setQuery(event.target.value)}
			/>
			{typed ? (
				<div className="item-tag-cards__results" aria-live="polite">
					{loading ? <Skeleton variant="line" count={1} /> : null}
					{!loading && !found.length ? <p className="meta-line">No item tag of this card matches.</p> : null}
					{!loading && results.length && anyMovable ? (
						<ul className="tag-tree item-tag-cards__found" aria-label={`Item tags of ${cardName} matching “${typed}”`}>
							{results.map(tag => {
								const place = placeOf(tag);
								const menu = movable(tag) ? buildActionMenu([{
									key: 'move',
									group: 'record',
									icon: <DIAGRAM_2 />,
									label: 'Move to…',
									onClick: () => setMoving(tag),
								}]) : null;
								return (
									<li key={tagIdOf(tag)}>
										<div className="tag-tree__row" data-testid="item-tag-result" data-tag-id={tagIdOf(tag)}>
											<div className="tag-tree__main">
												<span className="tag-tree__head">
													<TagBadge tag={tag} resolveItemTag={resolveItemTag} to={itemLink(tag)} />
												</span>
												{place ? <span className="tag-tree__description" title={place}>{place}</span> : null}
											</div>
											{menu ? (
												<span className="tag-tree__row-actions">
													<Dropdown menu={menu} trigger={['click']} placement="bottomRight">
														<Button type="text" icon={<KEBAB />} aria-label={`Actions for ${titleOf(tag)}`} />
													</Dropdown>
												</span>
											) : null}
										</div>
									</li>
								);
							})}
						</ul>
					) : null}
					{!loading && results.length && !anyMovable ? (
						<TagRow
							tags={results}
							label={`Item tags of ${cardName} matching “${typed}”`}
							renderTag={tag => (
								<TagBadge
									tag={tag}
									resolveItemTag={resolveItemTag}
									to={itemLink(tag)}
								/>
							)}
						/>
					) : null}
					{!loading && more ? <p className="meta-line">More match. Type more of the title.</p> : null}
				</div>
			) : null}
			{moving ? (
				<TagMoveModal
					open
					onClose={() => setMoving(null)}
					onMove={moveTo}
					tag={moving}
					name={titleOf(moving)}
					targets={itemTagPlaceTargets(tags, moving)}
					note="An item tag is always the last level: it goes under a tag at the first or second level, or at the top."
					refusalOf={itemTagMoveRefusal}
				/>
			) : null}
		</li>
	);
}

/**
 * `tags` — this collection's OWN tags (the places an item tag of its items can
 * go); `canManage` — may move them (manage, and the collection not archived).
 * `error` / `onRetry` — the vocabulary read failed: until it has answered that
 * is an error with Retry, never "No card here uses its items as tags".
 */
export default function ItemTagCardRows({ collectionId, rows = [], loading = false, error = null, onRetry, ancestors = [], tags = [], canManage = false }) {
	const id = normalizeId(collectionId);
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById || null;
	const nameOf = ownerId => (ancestors || []).find(a => normalizeId(a._id) === ownerId)?.name || 'the parent collection';
	const ordered = useMemo(() => {
		const list = Array.isArray(rows) ? rows : [];
		// This collection's own first, then its ancestors'.
		return [...list.filter(row => normalizeId(row?.collection_id) === id), ...list.filter(row => normalizeId(row?.collection_id) !== id)];
	}, [rows, id]);

	return (
		<section className="form-panel item-tag-cards" data-testid="item-tag-cards">
			<div className="micro-label form-panel__kicker">Item tags</div>
			<p className="meta-line item-tag-cards__lead">
				Cards whose items can be put on other items as tags. They are not listed one by one: find one by its item’s title.
			</p>
			{!ordered.length && (loading || error) ? (
				<TabLoadState loading={loading} error={error} label="item tags" onRetry={onRetry} rows={1} />
			) : null}
			{ordered.length && error ? <RefreshFailedNotice label="item tags" onRetry={onRetry} /> : null}
			{!loading && !error && !ordered.length ? (
				<p className="item-tag-cards__empty">
					No card here uses its items as tags. Turn it on for a card on the{' '}
					<Link to={`/collections/${id}/cards`}>Cards tab</Link>, with the tag beside it.
				</p>
			) : null}
			{ordered.length ? (
				<ul className="item-tag-cards__list">
					{ordered.map(row => {
						const owner = normalizeId(row?.collection_id);
						return (
							<CardRow
								key={`${owner}:${normalizeId(row?.card_id)}`}
								row={row}
								collectionId={id}
								ownerName={owner && owner !== id ? nameOf(owner) : null}
								cardsById={cardsById}
								tags={tags}
								canManage={canManage}
							/>
						);
					})}
				</ul>
			) : null}
		</section>
	);
}
